import React, { useState, useEffect, useRef } from 'react';
import FurtherLearning from './FurtherLearning';
import GenerateButtons from './GenerateButtons';
import FreeTextAnswerForm from './FreeTextAnswerForm';
import { useAuthFetch } from '../../hooks/useAuthFetch';
import { useRoles } from '../../context/RolesContext';

// Strip markdown link syntax [text](url) left by OpenAI citation injection
const stripMarkdownLinks = (text) =>
  typeof text === 'string' ? text.replace(/\[([^\]]+)\]\(https?:\/\/[^)]+\)/g, '$1').replace(/\s*\(https?:\/\/\S+\)/g, '').trim() : text;

function QuestionsList({ questions, selectedTopic, selectedDomain, isRandomMode, onGenerateQuestion, generatingQuestion,
  isAdmin, onBatchGenerate, batchGenerating, batchProgress, goalPace, questionsLoading }) {
  const { authFetch } = useAuthFetch();
  const { generationPaused } = useRoles();
  const prefetchRef = useRef(null);
  const [currentQuestion, setRandomQuestion] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState('');
  const [isAnswerVisible, setIsAnswerVisible] = useState(false);
  const [answeredCorrectly, setAnsweredCorrectly] = useState(false);
  const [userAnswerId, setUserAnswerId] = useState(null);
  const [submittedConfidence, setSubmittedConfidence] = useState(false);
  const [showFurtherLearning, setShowFurtherLearning] = useState(false);
  const [totalConfidence, setTotalConfidence] = useState(0);
  const [similarity, setSimilarity] = useState(null);
  const [sentTypedAnswer, setSentTypedAnswer] = useState(false);
  const [localAnsweredIds, setLocalAnsweredIds] = useState(new Set());
  const [fetchingYoutube, setFetchingYoutube] = useState(false);
  const [checkingValidity, setCheckingValidity] = useState(false);
  const [validityResult, setValidityResult] = useState(null);
  const [showValidityNote, setShowValidityNote] = useState(false);
  const [flagOpen, setFlagOpen] = useState(false);
  const [flagText, setFlagText] = useState('');
  const [flagSubmitting, setFlagSubmitting] = useState(false);
  const [flagDone, setFlagDone] = useState(false);

  useEffect(() => {
    if (similarity !== null && similarity >= 0.9) {
      setSelectedAnswer(currentQuestion.correct_answer);
    }
  }, [similarity, currentQuestion?.correct_answer]);

  const parseQuestion = (question) => {
    if (!question) return null;
    return {
      ...question,
      options: typeof question.options === 'string' ? JSON.parse(question.options) : question.options,
      incorrect_explanations: typeof question.incorrect_explanations === 'string' ? JSON.parse(question.incorrect_explanations) : question.incorrect_explanations,
      key_terms: typeof question.key_terms === 'string' ? JSON.parse(question.key_terms) : (question.key_terms || []),
    };
  };

  useEffect(() => {
    prefetchRef.current = null;
    if (questions && questions.length > 0) {
      setRandomQuestion(parseQuestion(questions[0]));
      setIsAnswerVisible(false);
    }
    setSubmittedConfidence(false);
    setTotalConfidence(questions.reduce((acc, q) => acc + q.last_confidence, 0));
    setShowFurtherLearning(false);
    setSentTypedAnswer(false);
    setLocalAnsweredIds(new Set());
    setIsAnswerVisible(false);
    setSimilarity(null);
    setValidityResult(null);
    setCheckingValidity(false);
    setShowValidityNote(false);
    setFlagOpen(false);
    setFlagText('');
    setFlagSubmitting(false);
    setFlagDone(false);
  }, [questions]);

  useEffect(() => {
    if (selectedDomain) { setRandomQuestion(null); prefetchRef.current = null; }
  }, [selectedDomain]);

  if (!currentQuestion) {
    if (questionsLoading) {
      return (
        <div className="study-empty">
          <span className="study-empty-icon">⏳</span>
          <p className="study-empty-text">Loading questions…</p>
        </div>
      );
    }
    return (
      <div className="study-empty">
        <span className="study-empty-icon">📚</span>
        <p className="study-empty-text">
          {isRandomMode
            ? 'No questions found.'
            : selectedTopic
              ? 'No questions yet for this topic.'
              : 'Select a domain, group, and topic to start studying'}
        </p>
        {selectedTopic && (
          <GenerateButtons
            onGenerateQuestion={onGenerateQuestion}
            generatingQuestion={generatingQuestion}
            isAdmin={isAdmin}
            generationPaused={generationPaused}
            onBatchGenerate={onBatchGenerate}
            batchGenerating={batchGenerating}
            batchProgress={batchProgress}
          />
        )}
      </div>
    );
  }

  const deleteQuestion = () => {
    if (!window.confirm('Are you sure you want to delete this question?')) return;
    authFetch(`${process.env.REACT_APP_API_URL}/api/questions/${currentQuestion.id}`, { method: 'DELETE' })
      .then((res) => res.json())
      .then((data) => alert(data.message))
      .catch((err) => { console.error('Error deleting question:', err); alert('Error deleting question.'); });
  };

  const deleteThenGenerate = async () => {
    // Replace = delete first, then generate. While generation is paused the
    // generate half cannot succeed, so going ahead would HARD-delete the question
    // and every user's answers to it (deleteQuestion.js does not soft-delete) and
    // put nothing in its place. Refuse before touching anything. The button is
    // also hidden while paused; this is the guard that survives a stale flag.
    if (generationPaused) {
      alert('New question generation is paused, so this question cannot be replaced right now. Nothing was deleted.');
      return;
    }
    if (!window.confirm('Are you sure you want to delete this question?')) return;
    await authFetch(`${process.env.REACT_APP_API_URL}/api/questions/${currentQuestion.id}`, { method: 'DELETE' });
    onGenerateQuestion();
  };

  const fetchYoutubeLinks = async () => {
    setFetchingYoutube(true);
    try {
      const res = await authFetch(
        `${process.env.REACT_APP_API_URL}/api/questions/${currentQuestion.id}/fetch-youtube`,
        { method: 'POST' }
      );
      const data = await res.json();
      setRandomQuestion(prev => ({
        ...prev,
        youtube_link: data.youtube_link,
        short_youtube_video: data.short_youtube_video,
      }));
      // issue #95: alert on the link this button exists to fill, not on "both
      // are empty". The button renders exactly when youtube_link is missing, and
      // the backend now preserves an already-cached short video — so the
      // both-empty test stopped firing on the one case that matters (top still
      // missing), leaving the button a silent no-op that still spends quota.
      if (!data.youtube_link) {
        alert('No YouTube video found for this question.');
      }
    } catch (err) {
      console.error('Error fetching YouTube links:', err);
      alert('Failed to fetch YouTube links.');
    } finally {
      setFetchingYoutube(false);
    }
  };

  const handleUpdateAnswer = async () => {
    const suggested = validityResult?.Correct_answer;
    if (!suggested) return;
    try {
      const res = await authFetch(
        `${process.env.REACT_APP_API_URL}/api/admin/questions/${currentQuestion.id}/answer`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ correct_answer: suggested }),
        }
      );
      const data = await res.json();
      if (data.ok) {
        setRandomQuestion(prev => ({ ...prev, correct_answer: data.correct_answer }));
        setValidityResult(prev => ({ ...prev, _answerUpdated: true }));
      }
    } catch (err) {
      console.error('Update answer failed:', err);
    }
  };

  const handleCheckValidity = async () => {
    setCheckingValidity(true);
    setValidityResult(null);
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 160000);
    try {
      const res = await authFetch(
        `${process.env.REACT_APP_API_URL}/api/admin/check-validity/${currentQuestion.id}`,
        { method: 'POST', signal: controller.signal }
      );
      const data = await res.json();
      setValidityResult(data);
      if (!data.deleted) {
        setRandomQuestion(prev => ({ ...prev, last_validated: new Date().toISOString() }));
      }
    } catch (err) {
      console.error('Validity check failed:', err);
    } finally {
      clearTimeout(timeout);
      setCheckingValidity(false);
    }
  };

  const handleFlag = async () => {
    if (!flagText.trim()) return;
    setFlagSubmitting(true);
    try {
      const res = await authFetch(
        `${process.env.REACT_APP_API_URL}/api/questions/${currentQuestion.id}/flag`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ message: flagText.trim() }),
        }
      );
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      setFlagDone(true);
      setFlagOpen(false);
      setFlagText('');
    } catch (err) {
      console.error('Flag failed:', err);
    } finally {
      setFlagSubmitting(false);
    }
  };

  const sendAnswer = () => {
    setIsAnswerVisible(true);
    setAnsweredCorrectly(selectedAnswer === currentQuestion.correct_answer);
    setShowFurtherLearning(true);
    setLocalAnsweredIds(prev => new Set([...prev, currentQuestion.id]));
    authFetch(`${process.env.REACT_APP_API_URL}/api/questions/${currentQuestion.id}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ answer: selectedAnswer, correct_answer: currentQuestion.correct_answer, is_correct: selectedAnswer === currentQuestion.correct_answer }),
    })
      .then((res) => res.json())
      .then((data) => { setUserAnswerId(data.userAnswerId); triggerPrefetch(); })
      .catch((err) => { console.error('Error submitting answer:', err); alert('Error submitting answer.'); });
  };

  const rateConfidence = (confidenceValue) => {
    authFetch(`${process.env.REACT_APP_API_URL}/api/confidence/${currentQuestion.id}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userAnswerId, confidence: parseInt(confidenceValue, 10) }),
    })
      .then((res) => res.json())
      .then(() => {
        setSubmittedConfidence(true);
        setTotalConfidence(prev => prev - currentQuestion.last_confidence + parseInt(confidenceValue, 10));
      })
      .catch((err) => { console.error('Error submitting confidence:', err); alert('Error submitting confidence level.'); });
  };

  const triggerPrefetch = () => {
    prefetchRef.current = null;
    const url = isRandomMode
      ? `${process.env.REACT_APP_API_URL}/api/questions/random`
      : `${process.env.REACT_APP_API_URL}/api/questions/${selectedTopic}`;
    authFetch(url)
      .then(res => res.json())
      .then(data => {
        if (!data?.[0]) return;
        const parsed = parseQuestion(data[0]);
        parsed.options = [...parsed.options].sort(() => Math.random() - 0.5);
        prefetchRef.current = { question: parsed, allQuestions: data };
      })
      .catch(() => {});
  };

  const loadNewQuestion = () => {
    setRandomQuestion(null);
    setSelectedAnswer('');
    setIsAnswerVisible(false);
    setAnsweredCorrectly(false);
    setUserAnswerId(null);
    setSubmittedConfidence(false);
    setShowFurtherLearning(false);
    setSentTypedAnswer(false);
    setSimilarity(null);
    setValidityResult(null);
    setFlagOpen(false);
    setFlagText('');
    setFlagSubmitting(false);
    setFlagDone(false);
    const prefetched = prefetchRef.current;
    prefetchRef.current = null;
    if (prefetched) {
      setRandomQuestion(prefetched.question);
      return;
    }
    const url = isRandomMode
      ? `${process.env.REACT_APP_API_URL}/api/questions/random`
      : `${process.env.REACT_APP_API_URL}/api/questions/${selectedTopic}`;
    authFetch(url)
      .then((res) => res.json())
      .then((data) => {
        const parsed = parseQuestion(data[0]);
        const shuffled = [...parsed.options].sort(() => Math.random() - 0.5);
        parsed.options = shuffled;
        setRandomQuestion(parsed);
      })
      .catch((err) => console.error('Error fetching questions:', err));
  };

  const allAnswered = questions.length > 0 &&
    questions.every(q => new Date(q.last_answered_at) > new Date('1970-01-02') || localAnsweredIds.has(q.id));

  const isAnsweredToday = (() => {
    if (!currentQuestion) return false;
    const lat = currentQuestion.last_answered_at;
    if (!lat) return false;
    const answered = new Date(lat);
    const today = new Date();
    return (
      answered.getFullYear() === today.getFullYear() &&
      answered.getMonth() === today.getMonth() &&
      answered.getDate() === today.getDate()
    );
  })();

  const totalQuestions = questions.length;
  const neededConfidence = totalQuestions * 3;
  const confidencePct = neededConfidence > 0 ? Math.min((totalConfidence / neededConfidence) * 100, 100) : 0;

  const goalConfidenceTarget = goalPace ? Math.round(goalPace.timePct * neededConfidence) : null;
  const goalMetPct = goalConfidenceTarget > 0 ? Math.min((totalConfidence / goalConfidenceTarget) * 100, 100) : 0;

  let lastAnsweredAt = new Date(currentQuestion.last_answered_at);
  lastAnsweredAt = lastAnsweredAt.getFullYear() < 2000 ? 'Never' : lastAnsweredAt.toLocaleDateString();

  const missingFields = [
    !currentQuestion.explanation && 'explanation',
    !currentQuestion.youtube_link && 'youtube link',
    !currentQuestion.incorrect_explanations && 'incorrect answer explanations',
  ].filter(Boolean);

  return (
    <div>
      {/* Stats bar */}
      <div className="study-stats-bar">
        <div className="study-stat">
          <span className="study-stat-value">{totalQuestions}</span>
          <span className="study-stat-label">Questions</span>
        </div>
        <div className="study-stat">
          <span className="study-stat-value">
            {totalConfidence}
            <span style={{ fontSize: 13, color: '#475569', fontFamily: 'Outfit,sans-serif' }}> / {neededConfidence}</span>
          </span>
          <span className="study-stat-label">Confidence</span>
          <div className="study-stat-bar">
            <div className="study-stat-bar-fill" style={{ width: `${confidencePct}%` }} />
          </div>
        </div>
        {goalConfidenceTarget !== null && (
          <div className="study-stat">
            <span className="study-stat-value">
              {totalConfidence}
              <span style={{ fontSize: 13, color: '#475569', fontFamily: 'Outfit,sans-serif' }}> / {goalConfidenceTarget}</span>
            </span>
            <span className="study-stat-label">Today's Goal</span>
            <div className="study-stat-bar">
              <div className="study-stat-bar-fill study-stat-bar-fill--goal" style={{ width: `${goalMetPct}%` }} />
            </div>
          </div>
        )}
      </div>

      {/* Question card */}
      <div className="study-question-card">
        {isAnsweredToday && (
          <div className="study-already-answered-banner">
            <span className="study-already-answered-icon">☀</span>
            <span className="study-already-answered-text">
              You've already studied this question today — consider taking a break!
            </span>
          </div>
        )}
        <div className="study-question-meta">
          <span className="study-question-id">Q#{currentQuestion.id}</span>
          <span className="study-question-last-answered">Last answered: {lastAnsweredAt}</span>
          {isRandomMode && currentQuestion.topic_name && (
            <span className="study-question-topic-badge">
              {currentQuestion.domain_name} · {currentQuestion.topic_name}
            </span>
          )}
        </div>

        <p className="study-question-text">{currentQuestion.question_text}</p>

        {/* Free-text answer */}
        <FreeTextAnswerForm
          key={currentQuestion.id}
          correctAnswer={currentQuestion.correct_answer}
          onDone={(sim) => { setSimilarity(sim); setSentTypedAnswer(true); triggerPrefetch(); }}
          disabled={isAnswerVisible}
        />

        {/* Radio options */}
        {Array.isArray(currentQuestion.options) && sentTypedAnswer && (
          <div className="study-options">
            {currentQuestion.options.map((opt, idx) => {
              let cls = 'study-option';
              if (isAnswerVisible) {
                if (opt === currentQuestion.correct_answer) cls += ' correct';
                else if (opt === selectedAnswer && opt !== currentQuestion.correct_answer) cls += ' incorrect';
              } else if (selectedAnswer === opt) {
                cls += ' selected';
              }
              return (
                <label key={idx} className={cls}>
                  <input
                    className="study-option-radio"
                    type="radio"
                    name={`question-${currentQuestion.id}`}
                    value={opt}
                    checked={selectedAnswer === opt}
                    onChange={() => !isAnswerVisible && setSelectedAnswer(opt)}
                    disabled={isAnswerVisible}
                    data-form-type="other"
                  />
                  <span className="study-option-text">{opt}</span>
                </label>
              );
            })}
          </div>
        )}

        {/* Submit answer button */}
        {sentTypedAnswer && !isAnswerVisible && (
          <button className="study-btn-primary" disabled={!selectedAnswer} onClick={sendAnswer}>
            Submit Answer
          </button>
        )}

        {/* Answer reveal */}
        {isAnswerVisible && (
          <div className="study-answer-reveal">
            <p className="study-answer-reveal-label">Correct Answer</p>
            <p className="study-answer-reveal-text">{currentQuestion.correct_answer}</p>
            {currentQuestion.docs_url && (
              <a className="study-answer-docs-link" href={currentQuestion.docs_url} target="_blank" rel="noopener noreferrer">
                Documentation ↗
              </a>
            )}
          </div>
        )}

        {/* Correct banner + confidence */}
        {answeredCorrectly && isAnswerVisible && (
          <div className="study-correct-banner">
            <span className="study-correct-icon">🎯</span>
            <div style={{ flex: 1 }}>
              <p className="study-correct-title">Correct!</p>
              {!submittedConfidence ? (
                <>
                  <p className="study-correct-sub">How confident were you? (Previous: {currentQuestion.last_confidence})</p>
                  <div className="study-confidence-btns">
                    <button className="study-confidence-btn low" onClick={() => rateConfidence(1)}>Low</button>
                    <button className="study-confidence-btn medium" onClick={() => rateConfidence(2)}>Medium</button>
                    <button className="study-confidence-btn high" onClick={() => rateConfidence(3)}>High</button>
                  </div>
                </>
              ) : (
                <p className="study-confidence-recorded">Confidence recorded ✓</p>
              )}
            </div>
          </div>
        )}

        {/* Explanations */}
        {isAnswerVisible && currentQuestion.explanation && (
          <div className="study-explanation">
            <p className="study-explanation-title">Explanation</p>
            <p className="study-explanation-text">{stripMarkdownLinks(currentQuestion.explanation)}</p>

            {currentQuestion.example && (
              <>
                <p className="study-explanation-title" style={{ marginTop: 16 }}>Example</p>
                <div className="further-example">
                  <p>{currentQuestion.example}</p>
                </div>
              </>
            )}

            {Array.isArray(currentQuestion.incorrect_explanations) && currentQuestion.incorrect_explanations.length > 0 && (
              <>
                <p className="study-explanation-title" style={{ marginTop: 16 }}>Why the other answers are wrong</p>
                {currentQuestion.incorrect_explanations.map((exp, idx) => (
                  <div key={idx} className="study-incorrect-item">
                    <p>{stripMarkdownLinks(exp)}</p>
                  </div>
                ))}
              </>
            )}

            {Array.isArray(currentQuestion.key_terms) && currentQuestion.key_terms.length > 0 && (
              <div className="study-key-terms">
                <p className="study-explanation-title" style={{ marginTop: 16 }}>Key Terms</p>
                {currentQuestion.key_terms.map((kt, idx) => (
                  <div key={idx} className="study-key-term-item">
                    <span className="study-key-term-name">{kt.term}</span>
                    <span className="study-key-term-def">{kt.definition}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Action row */}
        {isAnswerVisible && (
          <div className="study-action-row">
            <button className="study-btn-next" onClick={loadNewQuestion}>Next Question →</button>
          </div>
        )}

        {/* Quality warning */}
        {isAnswerVisible && missingFields.length > 0 && (
          <div className="study-quality-warning">
            <div>
              <p className="study-quality-warning-text">
                <strong>Missing: </strong>{missingFields.join(', ')}
              </p>
              <div className="study-action-row" style={{ marginTop: 10 }}>
                <button className="study-btn-danger" onClick={deleteQuestion}>Delete</button>
                {!generationPaused && (
                  <button className="study-btn-danger" onClick={deleteThenGenerate} disabled={generatingQuestion}>
                    {generatingQuestion ? 'Working…' : 'Replace'}
                  </button>
                )}
                {!currentQuestion.youtube_link && (
                  <button className="study-btn-sm" onClick={fetchYoutubeLinks} disabled={fetchingYoutube}>
                    {fetchingYoutube ? 'Fetching…' : 'Fetch YouTube'}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Admin: ad-hoc validity check */}
        {isAdmin && (
          <div className="study-validity-check">
            <div className="study-validity-header">
              <button className="study-btn-sm" onClick={handleCheckValidity} disabled={checkingValidity}>
                {checkingValidity ? 'Checking…' : '⟳ Check Validity'}
              </button>
              <button className="study-btn-danger study-btn-danger--sm" onClick={deleteQuestion}>
                Delete
              </button>
              <span className="study-validity-note">
                Last validated: {currentQuestion.last_validated
                  ? new Date(currentQuestion.last_validated).toLocaleDateString()
                  : 'Never'}
              </span>
              {currentQuestion.last_validity_note && !validityResult && (
                <button className="study-btn-sm" onClick={() => setShowValidityNote(v => !v)}>
                  {showValidityNote ? 'Hide note' : 'AI note'}
                </button>
              )}
            </div>
            {showValidityNote && currentQuestion.last_validity_note && !validityResult && (
              <p className="study-validity-explanation">{currentQuestion.last_validity_note}</p>
            )}
            {checkingValidity && (
              <p className="study-validity-note">Running web search + AI — may take up to 2 min</p>
            )}
            {validityResult && !checkingValidity && (
              <div className={`study-validity-result${validityResult.deleted ? ' study-validity-result--fail' : ' study-validity-result--pass'}`}>
                <p className="study-validity-score">
                  {validityResult.deleted ? '✗ Failed' : '✓ Passed'} &mdash; Answer validity: {validityResult.Answer_validity}/100
                </p>
                <p className="study-validity-explanation">{validityResult.Explanation}</p>
                {validityResult.Correct_answer && (
                  <div className="study-validity-answer-row">
                    <p className="study-validity-suggested-label">AI suggested answer:</p>
                    <p className="study-validity-suggested-text">{validityResult.Correct_answer}</p>
                    {!validityResult.deleted && (
                      validityResult._answerUpdated
                        ? <p className="study-validity-updated">Answer updated ✓</p>
                        : validityResult.Correct_answer !== currentQuestion.correct_answer && (
                          <button className="study-btn-sm" onClick={handleUpdateAnswer}>Apply Answer</button>
                        )
                    )}
                  </div>
                )}
                {validityResult.deleted && (
                  <p className="study-validity-deleted">Question deleted and logged to feedback.</p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Flag question */}
        <div className="study-flag-section">
          {flagDone ? (
            <p className="study-flag-done">⚑ Flagged — thanks for the report.</p>
          ) : flagOpen ? (
            <div className="study-flag-form">
              <textarea
                className="study-flag-textarea"
                placeholder="Describe the issue (wrong answer, outdated content, typo…)"
                value={flagText}
                onChange={e => setFlagText(e.target.value)}
                maxLength={1000}
                rows={3}
              />
              <div className="study-flag-actions">
                <button
                  className="study-btn-sm"
                  onClick={handleFlag}
                  disabled={flagSubmitting || !flagText.trim()}
                >
                  {flagSubmitting ? 'Submitting…' : 'Submit'}
                </button>
                <button
                  className="study-flag-cancel"
                  onClick={() => { setFlagOpen(false); setFlagText(''); }}
                  disabled={flagSubmitting}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button className="study-flag-btn" onClick={() => setFlagOpen(true)}>
              ⚑ Flag an issue
            </button>
          )}
        </div>
      </div>

      {/* Generate question — only shown once all existing questions have been answered */}
      {allAnswered && !isRandomMode && (
        <GenerateButtons
          onGenerateQuestion={onGenerateQuestion}
          generatingQuestion={generatingQuestion}
          isAdmin={isAdmin}
          generationPaused={generationPaused}
          onBatchGenerate={onBatchGenerate}
          batchGenerating={batchGenerating}
          batchProgress={batchProgress}
        />
      )}
      {allAnswered && isRandomMode && (
        <div className="study-generate-area">
          <button className="study-btn-generate" onClick={loadNewQuestion}>
            Load More Questions
          </button>
        </div>
      )}

      {/* Further Learning */}
      {showFurtherLearning && (
        <FurtherLearning
          Question={currentQuestion}
          selectedTopic={isRandomMode ? String(currentQuestion.topic_id) : selectedTopic}
        />
      )}
    </div>
  );
}

export default QuestionsList;
